/**
 * @file TranslationProcessor.ts
 *
 * @description
 * Client-side wrapper that translates recognized speech text via our own authenticated backend
 * (`POST /session/{sessionId}/translate` on rosetta-server), which in turn calls Azure with the
 * server-held subscription key. The bare Azure key no longer lives in the browser.
 *
 * ## Role in the pipeline
 * Intended position: SpeechProcessor (recognition) → TranslationProcessor
 * (translation) → Text2SpeechProcessor (TTS) → AudioPlayer (playback).
 * Callers enqueue finalized recognized-text strings via `enqueueText()`; the processor fires one
 * batched backend request per utterance and delivers results through `onTranslationReady` with a
 * `Map<Language, TranslationResult>`.
 *
 * ## Translate-ASAP, one request per utterance
 * No queue or polling interval. `enqueueText()` immediately groups the output languages by their
 * (glossary-marked) source text and sends a single request carrying all groups; the backend issues
 * one Azure multi-`to` call per group and returns all translations together.
 *
 * ## Dynamic Dictionary glossary
 * When English is one side of a translation pair (en→X or X→en), glossary terms are injected as
 * `<mstrans:dictionary>` XML markup in the source text, forcing the API to use specific target
 * translations for those terms. Because the markup embeds a single per-target word, languages whose
 * marked text differs end up in separate groups (and thus separate Azure calls); the common case
 * (no term matched, or a language-invariant proper noun) collapses to one group / one Azure call.
 * For non-English pairs the text is sent unchanged (Dynamic Dictionary requires English on one side
 * per Azure API constraint).
 *
 * ## Authentication
 * Authenticates via the shared `execWithToken` flow (MSAL bearer token → our backend). No Azure key.
 */
import {execWithToken} from "../utils/FetchUtils";
import {RosettaClient, TranslationGroup} from "../api/RosettaClient";
import {
    GlossaryTerm,
    Language,
    RecognizableAndTranslatableLanguage,
    TranslatableAndSpeakableLanguage,
    TranslationResult
} from "./Configuration";

export class TranslationProcessor {

    private readonly sessionId: string;

    private readonly inputLanguage: RecognizableAndTranslatableLanguage;
    private readonly outputLanguages: TranslatableAndSpeakableLanguage[];
    private readonly glossaryTerms: GlossaryTerm[];
    private readonly onTranslationReady: (translationsReady: Map<Language, TranslationResult>) => void;

    private readonly sourceLangCode: string;

    private isRunning = false;

    constructor(
        sessionId: string,
        inputLanguage: RecognizableAndTranslatableLanguage,
        outputLanguages: TranslatableAndSpeakableLanguage[],
        glossaryTerms: GlossaryTerm[] = [],
        onTranslationReady: (translationsReady: Map<Language, TranslationResult>) => void
    ) {
        this.sessionId = sessionId;

        this.inputLanguage = inputLanguage;
        this.outputLanguages = outputLanguages;
        this.glossaryTerms = glossaryTerms;
        this.onTranslationReady = onTranslationReady;

        this.sourceLangCode = inputLanguage.recognitionKey.split('-')[0];
    }

    public start = (): void => {
        this.isRunning = true;
    }

    public stop = (): void => {
        this.isRunning = false;
        console.log("[Translation Processor] Stopped");
    }

    public enqueueText = (text: string): void => {
        if (!this.isRunning) return;

        const groups = this.buildGroups(text);
        console.log('[Translation Processor] Translating', this.sourceLangCode, '->',
            this.outputLanguages.map(l => l.translationKey).join(','), 'in', groups.length, 'group(s)');

        execWithToken(token =>
            RosettaClient.translate(token, this.sessionId, this.sourceLangCode, groups))
            .then(items => {
                const parsedTranslations = new Map<Language, TranslationResult>();
                items.forEach(item => {
                    const lang = {id: item.to} as Language;
                    parsedTranslations.set(lang, {
                        originalText: text,
                        translatedText: item.text,
                        status: '',
                        language: lang
                    });
                });
                if (parsedTranslations.size > 0) {
                    this.onTranslationReady(parsedTranslations);
                }
            })
            .catch(error => {
                console.error('[Translation Processor] Translation request failed', error);
            });
    }

    /**
     * Groups output languages by their (glossary-marked) source text. Each distinct marked text
     * becomes one group whose `to` lists every language that shares it, so the backend can translate
     * them in a single Azure multi-`to` call.
     */
    private buildGroups = (text: string): TranslationGroup[] => {
        const byMarkedText = new Map<string, string[]>();
        for (const lang of this.outputLanguages) {
            const markedText = this.applyDynamicDictionary(text, lang.translationKey);
            const langs = byMarkedText.get(markedText);
            if (langs) {
                langs.push(lang.translationKey);
            } else {
                byMarkedText.set(markedText, [lang.translationKey]);
            }
        }

        const groups: TranslationGroup[] = [];
        byMarkedText.forEach((to, markedText) => groups.push({text: markedText, to}));
        return groups;
    }

    private applyDynamicDictionary = (text: string, targetLangCode: string): string => {
        // Azure Dynamic Dictionary requires English on one side of THIS specific pair, so the check
        // is per target language — not a processor-wide flag. Otherwise a non-English target (e.g.
        // es→de) would wrongly get markup just because English is also among the session's outputs.
        const isEnglishPair = this.sourceLangCode === 'en' || targetLangCode === 'en';
        console.debug("-------> applying dynamic dictionary? " + isEnglishPair, this.glossaryTerms )

        if (!isEnglishPair) return text;

        let result = text;
        for (const term of this.glossaryTerms) {
            const targetTranslation = term.targets[targetLangCode];
            if (targetTranslation === undefined) continue;

            const escapedSource = this.escapeRegex(term.source);
            // Use \b only when the term starts/ends with a word character;
            // otherwise fall back to negative lookaround so terms ending with
            // special chars (e.g. "C++") are still matched at word boundaries.
            const startBoundary = /^\w/.test(term.source) ? '\\b' : '(?<!\\w)';
            const endBoundary = /\w$/.test(term.source) ? '\\b' : '(?!\\w)';
            const regex = new RegExp(`${startBoundary}${escapedSource}${endBoundary}`, 'gi');
            result = result.replace(
                regex,
                match => `<mstrans:dictionary translation="${targetTranslation}">${match}</mstrans:dictionary>`
            );
        }
        console.debug("-------> applying dynamic dictionary? ", result )
        return result;
    }

    private escapeRegex = (s: string): string => {
        return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }
}
